import { StyledFormControl } from 'components/AdminConsole/commonUI/styles'
import { PreviewAvatarsList } from 'components/CommonComponents/PreviewList'
import { Folder, Tag, Users } from 'lucide-react'
import { Box, Flex, FormErrorMessage } from '@chakra-ui/core'
import React, { useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import OptimizedGroupsTree from 'components/CommonComponents/OptimizedGroupsTree'
import UsersList from 'components/CommonComponents/UsersList'
import useLocaleText from 'components/useLocaleText'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import { PopFormValues } from '../../types'
import { SelectionMode } from 'components/CommonComponents/OptimizedGroupsTree/hooks/useSelection'
import TagsList from 'components/CommonComponents/TagsList'
import { NavButtonTabs, TabKey } from 'components/CommonComponents/AudienceTabs'
import { getNestedError } from '../utils'

interface PopRecipientsProps {
  groupsKey?: string
  usersKey?: string
  tagsKey?: string
}

export const PopRecipients = ({ groupsKey = 'groups', usersKey = 'users', tagsKey = 'tags' }: PopRecipientsProps) => {
  const { control, watch, setValue, clearError, getValues, errors } = useFormContext<PopFormValues>()
  const { retailUsersObject, groups: groupsObject, tags } = useSelector((state: RootState) => state.config.config)
  const t_users = useLocaleText('users')
  const t_groups = useLocaleText('groups')
  const t_tags = useLocaleText('tags')
  const [activeTab, setActiveTab] = useState<TabKey>('groups')

  const watchedGroups = (watch(groupsKey as keyof PopFormValues) as string[]) || []
  const watchedUsers = (watch(usersKey as keyof PopFormValues) as string[]) || []
  const watchedTags = (watch(tagsKey as keyof PopFormValues) as string[]) || []

  const selectedGroupIds = useMemo(() => watchedGroups || [], [watchedGroups])
  const selectedUsersIds = useMemo(() => watchedUsers || [], [watchedUsers])
  const selectedTagIds = useMemo(() => watchedTags || [], [watchedTags])

  const popGroups = useMemo(() => {
    return selectedGroupIds.map((id: string) => groupsObject[id])
  }, [groupsObject, selectedGroupIds])

  const popUsers = useMemo(() => {
    return selectedUsersIds.map((id: string) => retailUsersObject?.[id])
  }, [retailUsersObject, selectedUsersIds])

  const popTags = useMemo(() => {
    return selectedTagIds.map((id: string) => tags.find((tag) => tag.sk === id))
  }, [tags, selectedTagIds])

  const validateAudience = () => {
    const groups = (getValues(groupsKey as keyof PopFormValues) as string[]) || []
    const users = (getValues(usersKey as keyof PopFormValues) as string[]) || []
    const tags = (getValues(tagsKey as keyof PopFormValues) as string[]) || []
    const hasGroups = groups.length > 0
    const hasUsers = users.length > 0
    const hasTags = tags.length > 0
    if (!hasGroups && !hasUsers && !hasTags) {
      return 'At least one audience type must be selected'
    }

    return true
  }

  const handleGroupsSelectionChange = (ids: string[]) => {
    setValue(groupsKey as keyof PopFormValues, ids as any, true)
    clearError([groupsKey, usersKey, tagsKey] as any)
  }

  const handleUsersSelectionChange = (ids: string[]) => {
    setValue(usersKey as keyof PopFormValues, ids as any, true)
    clearError([groupsKey, usersKey, tagsKey] as any)
  }

  const handleTagsSelectionChange = (ids: string[]) => {
    setValue(tagsKey as keyof PopFormValues, ids as any, true)
    clearError([groupsKey, usersKey, tagsKey] as any)
  }

  const listHeight = 300

  const hasAudienceError =
    getNestedError(errors, groupsKey) || getNestedError(errors, usersKey) || getNestedError(errors, tagsKey)

  return (
    <StyledFormControl>
      <Flex width="100%" justifyContent="center">
        <NavButtonTabs value={activeTab} onChange={setActiveTab} />
      </Flex>

      <StyledFormControl my={4}>
        <PreviewAvatarsList
          icon={Users}
          label={t_users}
          items={popUsers.map((user: any) => ({
            name: `${user?.first_name} ${user?.last_name || ''}`,
            profile_img_url: user?.profile_img_url,
          }))}
          defaultValue="-"
          max={10}
        />

        <PreviewAvatarsList
          icon={Folder}
          label={t_groups}
          items={popGroups.map((group) => ({
            name: group?.name || '',
          }))}
          defaultValue="-"
          max={10}
          isGroup
        />

        <PreviewAvatarsList
          icon={Tag}
          label={t_tags}
          items={popTags.map((tag) => ({
            name: tag?.name || '',
          }))}
          defaultValue="-"
          max={10}
          isGroup
        />
      </StyledFormControl>

      <Box
        borderRadius="5px"
        bg="white"
        padding={{ base: '0px', md: '10px' }}
        border={hasAudienceError ? '1px solid red' : '1px solid #e0e0e0'}
        boxShadow={hasAudienceError ? '0px 0px 0px 1px red' : 'none'}
      >
        <Box display={activeTab === 'groups' ? 'block' : 'none'}>
          <Controller
            control={control}
            name={groupsKey as keyof PopFormValues}
            defaultValue={selectedGroupIds}
            rules={{
              validate: validateAudience,
            }}
            as={
              <OptimizedGroupsTree
                groups={groupsObject}
                allowedGroups={Object.keys(groupsObject)}
                showSearch
                listHeight={listHeight}
                initialSelectedIds={selectedGroupIds}
                onSelectionChange={handleGroupsSelectionChange}
                selectionMode={SelectionMode.MULTIPLE}
              />
            }
          />
        </Box>
        <Box display={activeTab === 'users' ? 'block' : 'none'}>
          <Controller
            control={control}
            name={usersKey as keyof PopFormValues}
            defaultValue={selectedUsersIds}
            rules={{
              validate: validateAudience,
            }}
            as={
              <UsersList
                showSearch
                listHeight={listHeight}
                initialSelectedIds={selectedUsersIds}
                selectionMode={SelectionMode.MULTIPLE}
                onSelectionChange={handleUsersSelectionChange}
              />
            }
          />
        </Box>

        <Box display={activeTab === 'tags' ? 'block' : 'none'}>
          <Controller
            control={control}
            name={tagsKey as keyof PopFormValues}
            defaultValue={selectedTagIds}
            rules={{
              validate: validateAudience,
            }}
            as={
              <TagsList
                showSearch
                listHeight={listHeight}
                initialSelectedIds={selectedTagIds}
                selectionMode={SelectionMode.MULTIPLE}
                onSelectionChange={handleTagsSelectionChange}
              />
            }
          />
        </Box>
      </Box>

      <StyledFormControl isInvalid={!!hasAudienceError}>
        {/* @ts-ignore */}
        <FormErrorMessage>
          {getNestedError(errors, groupsKey)?.message ||
            getNestedError(errors, usersKey)?.message ||
            getNestedError(errors, tagsKey)?.message}
        </FormErrorMessage>
      </StyledFormControl>
    </StyledFormControl>
  )
}
